import React, { useEffect, useMemo, useRef, useState } from "react";
import { T, statusColor, priorityColor } from "../theme/tokens.js";
import { Btn, Chip, Empty, Modal, Notice, Labelled } from "./ui/index.jsx";
import { fmtMoney, fmtDate } from "../lib/format.js";
import { REGISTERS, registerFields, registerColumns } from "../lib/registers.js";
import { useRegister } from "../lib/useRegister.js";
import { QuickPick, InlineEdit, inputStyle } from "./CellEdit.jsx";
import { isOverdue, effectiveDueDate } from "../lib/calc.js";
import { useAuth } from "../auth/AuthProvider.jsx";
import { buildWorkbook } from "../lib/exporters/xlsx.js";
import { registerSheet } from "../lib/exporters/register.js";
import { download, exportName, XLSX_TYPE } from "../lib/exporters/download.js";

/* A select that keeps a value it was never offered.
   Imported rows carry statuses no dropdown anticipated, and a form must not
   quietly rewrite one just because it is not in the list. */
function OpenSelect({ field, value, onChange }) {
  const options = useMemo(() => {
    const base = field.options ?? [];
    return value && !base.includes(value) ? [value, ...base] : base;
  }, [field.options, value]);

  return (
    <select style={inputStyle} value={value ?? ""} onChange={(e) => onChange(e.target.value || null)}>
      <option value="">Not set</option>
      {options.map((o) => <option key={o} value={o}>{o}</option>)}
    </select>
  );
}

function FieldInput({ field, value, onChange, autoFocus }) {
  if (field.type === "select") {
    return <OpenSelect field={field} value={value} onChange={onChange} />;
  }
  if (field.type === "textarea") {
    return (
      <textarea style={{ ...inputStyle, minHeight: 60, resize: "vertical" }}
        value={value ?? ""} onChange={(e) => onChange(e.target.value || null)} />
    );
  }
  if (field.type === "boolean") {
    return (
      <select style={inputStyle} value={value === true ? "yes" : value === false ? "no" : ""}
        onChange={(e) => onChange(e.target.value === "" ? null : e.target.value === "yes")}>
        <option value="">Not set</option>
        <option value="yes">Yes</option>
        <option value="no">No</option>
      </select>
    );
  }
  return (
    <input
      style={inputStyle}
      autoFocus={autoFocus}
      type={field.type === "number" ? "number" : field.type === "date" ? "date" : "text"}
      min={field.min} max={field.max}
      value={value ?? ""}
      onChange={(e) => {
        const raw = e.target.value;
        if (raw === "") return onChange(null);
        onChange(field.type === "number" ? Number(raw) : raw);
      }}
    />
  );
}

function RowForm({ registerKey, contract, row, onSave, onClose }) {
  const cfg = REGISTERS[registerKey];
  const fields = registerFields(registerKey, contract);
  const [draft, setDraft] = useState(row ?? {});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const missing = fields.filter((f) => f.required && !draft[f.key]);

  const submit = async () => {
    if (missing.length) { setError(`${missing[0].label} is required.`); return; }
    setBusy(true);
    const ok = await onSave(draft);
    setBusy(false);
    if (ok) onClose();
    else setError("That could not be saved. Check the values and try again.");
  };

  return (
    <Modal title={`${row ? "Edit" : "Add"} ${cfg.singular}`} onClose={onClose} wide>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 12 }}>
        {fields.map((f, i) => (
          <div key={f.key} style={{ gridColumn: f.type === "textarea" ? "1 / -1" : undefined }}>
            <Labelled label={f.label + (f.required ? " *" : "")} hint={f.hint}>
              <FieldInput field={f} value={draft[f.key]} autoFocus={i === 0}
                onChange={(v) => setDraft({ ...draft, [f.key]: v })} />
            </Labelled>
          </div>
        ))}
      </div>
      {error && <div style={{ marginTop: 12 }}><Notice kind="error">{error}</Notice></div>}
      <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 16 }}>
        <Btn kind="ghost" onClick={onClose}>Cancel</Btn>
        <Btn onClick={submit} disabled={busy}>{busy ? "Saving..." : "Save"}</Btn>
      </div>
    </Modal>
  );
}

/* Free-text columns worth changing without the form. Everything else that is
   text is prose, and prose belongs in the form where there is room for it. */
const QUICK_TEXT = new Set(["owner", "vendor", "reviewer", "contractor"]);

/* Columns that hold one short word and should never wrap or stretch. */
const CHIP_KEYS = new Set(["status", "priority", "deliverable_type", "rating",
                           "impact", "probability", "risk_level", "impact_level"]);
const MONEY_KEYS = new Set(["value", "amount", "rate"]);

/* A chip is for a label. A sentence in a chip is what turned the procurement
   table into a single line three screens wide, so anything long is set as
   ordinary text instead. */
const isLabel = (v) => String(v).length <= 22 && !/[.;:]/.test(String(v));

/* Long text is held to two lines. The whole thing is on hover and in the edit
   form, so nothing is lost, but one wordy row can no longer set the width of
   every column beside it. */
const Clamped = ({ children }) => (
  <span
    title={children}
    style={{
      display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical",
      overflow: "hidden", overflowWrap: "anywhere",
    }}
  >{children}</span>
);

const BLANK = <span style={{ color: T.line }}>-</span>;

/* What a cell looks like when nobody is editing it.
 *
 * `plain` turns off the overdue override, which belongs to the row and not to
 * the value. Without it every option in a status menu on a late deliverable
 * would read Overdue. */
function staticCell(field, row, registerKey, plain) {
  const v = row[field.key];
  if (v === null || v === undefined || v === "") return BLANK;

  if (field.key === "status") {
    const overdue = !plain && registerKey === "deliverables" && isOverdue(row);
    if (overdue) return <Chip color={T.risk}>Overdue</Chip>;
    return isLabel(v)
      ? <Chip color={statusColor(v)}>{v}</Chip>
      : <Clamped>{String(v)}</Clamped>;
  }
  if (field.key === "priority") return <Chip color={priorityColor(v)}>{v}</Chip>;
  if (field.key === "deliverable_type") {
    return <Chip color={v === "CD" ? T.navy : T.soft}>{v}</Chip>;
  }
  if (CHIP_KEYS.has(field.key) && isLabel(v)) return <Chip>{String(v)}</Chip>;
  if (field.prefix === "$" || MONEY_KEYS.has(field.key)) return fmtMoney(v);
  if (field.suffix === "%") return `${v}%`;
  if (field.suffix === "days") return `Net ${v}`;
  if (typeof v === "boolean") return v ? "Yes" : "No";
  if (field.type === "date") return fmtDate(v);
  if (field.type === "number") return String(v);
  return <Clamped>{String(v)}</Clamped>;
}

/* Which columns can be changed in the table rather than in the form. */
export const isQuickField = (field) =>
  field.type === "select" || field.type === "date" || QUICK_TEXT.has(field.key);

function cellContent(field, row, registerKey, onQuick) {
  const v = row[field.key];
  const shown = staticCell(field, row, registerKey);
  if (!onQuick || field.readOnly || !isQuickField(field)) return shown;

  if (field.type === "select") {
    /* A value the dropdown never offered still belongs to the row, so it is
       added to the top of the list rather than quietly replaced. */
    const base = field.options ?? [];
    const options = v && !base.includes(v) ? [v, ...base] : base;
    if (!options.length) return shown;
    return (
      <QuickPick current={shown} value={v ?? null} options={options}
        renderOption={(o) => staticCell(field, { ...row, [field.key]: o }, registerKey, true)}
        onPick={(next) => onQuick(row, field.key, next)} />
    );
  }

  return (
    <InlineEdit value={v ?? ""} type={field.type === "date" ? "date" : "text"}
      display={shown} onSave={(next) => onQuick(row, field.key, next)} />
  );
}

/* Narrow columns hold a date, a number or a label and are sized to it. Wide
   ones hold prose and are capped, so the table fits the panel instead of the
   panel fitting the longest sentence anybody typed. */
function columnStyle(field, head) {
  const base = head
    ? { textAlign: "left", padding: "7px 10px", borderBottom: `2px solid ${T.navy}`,
        color: T.navy, fontSize: 11, fontWeight: 700, letterSpacing: "0.08em",
        textTransform: "uppercase", whiteSpace: "nowrap" }
    : { padding: "8px 10px", color: T.ink, verticalAlign: "top" };

  if (MONEY_KEYS.has(field.key) || field.prefix === "$") {
    return { ...base, whiteSpace: "nowrap", textAlign: "right" };
  }
  if (field.type === "date" || field.type === "number" || CHIP_KEYS.has(field.key)) {
    return { ...base, whiteSpace: "nowrap", width: 1 };
  }
  if (field.key === "title") {
    return { ...base, minWidth: 220, maxWidth: 360, overflowWrap: "anywhere" };
  }
  return { ...base, minWidth: 120, maxWidth: 240, overflowWrap: "anywhere" };
}

/* The container: fetches, saves, retires. */
export default function RegisterTable({ registerKey, contract, note, extraTop }) {
  const { rows, error, save, retire } = useRegister(registerKey, contract?.id);
  return (
    <RegisterTableView
      registerKey={registerKey} contract={contract} rows={rows}
      error={error} onSave={save} onRetire={retire} note={note} extraTop={extraTop}
    />
  );
}

/* The view: given rows, draws them. Separated so it can be rendered and looked
   at without a database behind it. */
export function RegisterTableView({
  registerKey, contract, rows, error, onSave, onRetire, note, extraTop,
}) {
  const cfg = REGISTERS[registerKey];
  const [editing, setEditing] = useState(null);
  /* A viewer sees the whole register and changes none of it. The database
     refuses their writes regardless; this is so the screen does not offer a
     button whose only outcome would be an error. */
  const { canEdit } = useAuth();
  const [adding, setAdding] = useState(false);
  const [confirmRetire, setConfirmRetire] = useState(null);
  const [query, setQuery] = useState("");

  const columns = registerColumns(registerKey, contract);
  const quickCount = columns.filter(isQuickField).length;

  /* What is on screen, not the whole register. If somebody has filtered down
     to one vendor and then exports, handing them everything is the wrong
     answer and a confusing one. */
  const toExcel = () => {
    const sheet = registerSheet(registerKey, contract, shown ?? []);
    download(buildWorkbook([sheet]), exportName(contract, registerKey, "xlsx"), XLSX_TYPE);
  };

  /* One field on one row, saved on the spot. It sends the whole row because
     that is what the form sends, so there is one save path and not two. */
  const quickSave = async (row, key, value) => { await onSave({ ...row, [key]: value }); };

  const shown = useMemo(() => {
    if (!rows) return null;
    if (!query.trim()) return rows;
    const q = query.toLowerCase();
    return rows.filter((r) =>
      Object.values(r).some((v) => v !== null && String(v).toLowerCase().includes(q))
    );
  }, [rows, query]);

  if (rows === null) return <Empty text="Loading..." />;

  return (
    <div>
      {error && <div style={{ marginBottom: 12 }}><Notice kind="error">{error}</Notice></div>}
      {extraTop}

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center",
                    marginBottom: 10, gap: 10, flexWrap: "wrap" }}>
        <span style={{ fontSize: 13, color: T.soft }}>
          {shown.length}{shown.length !== rows.length ? ` of ${rows.length}` : ""}
          {" "}{rows.length === 1 ? cfg.singular : cfg.label.toLowerCase()}
        </span>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          {rows.length > 8 && (
            <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Filter"
              style={{ ...inputStyle, width: 160, padding: "5px 9px", fontSize: 13 }} />
          )}
          {rows.length > 0 && (
            <Btn small kind="ghost" onClick={toExcel}>Export</Btn>
          )}
          {canEdit && (
            <Btn small kind="gold" onClick={() => setAdding(true)}>Add {cfg.singular}</Btn>
          )}
        </div>
      </div>

      {shown.length === 0 ? (
        <Empty text={query ? `Nothing matches "${query}".`
          : canEdit
            ? `No ${cfg.label.toLowerCase()} yet. Add one, or import a workbook once that arrives.`
            : `No ${cfg.label.toLowerCase()} yet.`} />
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr>
                {columns.map((f) => (
                  <th key={f.key} style={columnStyle(f, true)}>{f.label}</th>
                ))}
                {/* Edit and Remove stay pinned to the right edge. On a wide
                    register they used to scroll away underneath the text. */}
                <th style={{
                  borderBottom: `2px solid ${T.navy}`, position: "sticky", right: 0,
                  background: T.panel, borderLeft: `1px solid ${T.line}`, width: 1,
                }} />
              </tr>
            </thead>
            <tbody>
              {shown.map((row) => {
                const overdue = registerKey === "deliverables" && isOverdue(row);
                const rowBg = overdue ? "#FBF0EE" : T.panel;
                return (
                  <tr key={row.id} style={{
                    borderBottom: `1px solid ${T.line}`,
                    background: overdue ? T.risk + "0D" : "transparent",
                  }}>
                    {columns.map((f) => (
                      <td key={f.key} style={columnStyle(f, false)}>
                        {cellContent(f, row, registerKey, canEdit ? quickSave : null)}
                      </td>
                    ))}
                    <td style={{
                      padding: "8px 10px", textAlign: "right", whiteSpace: "nowrap",
                      position: "sticky", right: 0, background: rowBg,
                      borderLeft: `1px solid ${T.line}`, verticalAlign: "top",
                    }}>
                      {canEdit ? (
                        <>
                          <Btn small kind="ghost" onClick={() => setEditing(row)}>Edit</Btn>{" "}
                          <Btn small kind="danger" onClick={() => setConfirmRetire(row)}>Remove</Btn>
                        </>
                      ) : null}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {shown.length > 0 && quickCount > 0 && canEdit && (
        <div style={{ fontSize: 12, color: T.soft, marginTop: 10 }}>
          Anything with a dotted underline changes in the table. Click a status or a
          date and pick the new one. Edit opens the whole row.
        </div>
      )}

      {/* Said once, plainly, rather than leaving somebody to work out why
          there are no buttons and wonder whether the page is broken. */}
      {!canEdit && shown.length > 0 && (
        <div style={{ fontSize: 12, color: T.soft, marginTop: 10 }}>
          You have view access to this contract, so this register is read only.
          Ask Heidi if you need to be able to change it.
        </div>
      )}

      {note && <div style={{ fontSize: 12, color: T.soft, marginTop: 10 }}>{note}</div>}

      {(adding || editing) && (
        <RowForm registerKey={registerKey} contract={contract} row={editing}
          onSave={onSave} onClose={() => { setAdding(false); setEditing(null); }} />
      )}

      {confirmRetire && (
        <Modal title={`Remove this ${cfg.singular}?`} onClose={() => setConfirmRetire(null)}>
          <p style={{ fontSize: 14, lineHeight: 1.6, marginTop: 0 }}>
            <b>{confirmRetire.title}</b> will be taken off this register.
          </p>
          <Notice>
            It is retired rather than destroyed. The row stays in the database and can be
            brought back. Only Heidi can delete anything permanently.
          </Notice>
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 16 }}>
            <Btn kind="ghost" onClick={() => setConfirmRetire(null)}>Cancel</Btn>
            <Btn kind="danger" onClick={async () => {
              await onRetire(confirmRetire.id);
              setConfirmRetire(null);
            }}>Remove</Btn>
          </div>
        </Modal>
      )}
    </div>
  );
}
